import { ChallengeListItem } from '@/src/types/Challenge';
import { noop } from 'lodash';
import React, { Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import { ChallengeDetailsInfo, ChallengeInfo, ChallengeSetDetailsInfo } from '../components/root/ToolsContent';
import FacilitatorParticipantsPanel from '../components/tools/FacilitatorParticipantsPanel';
import JamChallengesDetail from '../components/tools/JamChallengeDetails';
import { ChallengeSet } from '../types/ChallengeSet';
import { IJamChallenge } from '../types/JamChallenges';
import { preProdLogger } from '../utils/log.utils';
import { useChallengeSet } from './challenge-set.context';
import { useChallenges } from './challenge.context';
import AITeammateChat from '@/src/components/ai-teammate-chat/AITeammateChat';

export interface ToolPanelContextValue {
  toolPanelContent: ReactNode | null;
  showToolPanel: boolean;
  toolPanelOpen: boolean;
  toolPanelWidth: number;
  setToolPanelWidth: Dispatch<SetStateAction<number>>;
  setHideAfterClose: Dispatch<SetStateAction<boolean>>;
  toggleToolPanel: (toggle: boolean) => void;
  toggleShowToolPanel: (toggle: boolean) => void;
  renderToolPanelContent: (content: ReactNode) => void;
  toggleChallengeInfo: (challengeItem: ChallengeListItem) => void;
  toggleChallengeDetailsInfo: (challenge: ChallengeListItem) => void;
  toggleChallengeSetDetailsInfo: (challenge: ChallengeSet) => void;
  toggleJamChallengeDetailsInfo: (challenge: IJamChallenge) => void;
  toggleFacilitatorParticipantsInfo: () => void;
  toggleAITeammateChat: (toogle: boolean) => void;
  aiTeammateChatIsVisible: boolean;
}

const ToolPanelContext = React.createContext<ToolPanelContextValue>({
  toolPanelContent: null,
  showToolPanel: false,
  toolPanelOpen: false,
  toolPanelWidth: 300,
  setToolPanelWidth: noop,
  setHideAfterClose: noop,
  toggleToolPanel: noop,
  toggleShowToolPanel: noop,
  renderToolPanelContent: noop,
  toggleChallengeInfo: noop,
  toggleChallengeDetailsInfo: noop,
  toggleChallengeSetDetailsInfo: noop,
  toggleJamChallengeDetailsInfo: noop,
  toggleFacilitatorParticipantsInfo: noop,
  toggleAITeammateChat: noop,
  aiTeammateChatIsVisible: false,
});

const ToolPanelProvider: React.FC = ({ children }) => {
  const [showToolPanel, setShowToolPanel] = useState(false);
  const [toolPanelOpen, setToolPanelOpen] = useState(false);
  const [toolPanelContent, setToolPanelContent] = useState<ReactNode>(null);
  const [toolPanelWidth, setToolPanelWidth] = useState(300);
  const [hideAfterClose, setHideAfterClose] = useState(false);
  // Identifies which panel's content is currently shown, so toggle-style entry points
  // (e.g. the Header AI Teammate button) can distinguish "my panel is open" from
  // "some other panel is open". Cleared whenever the panel is hidden.
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const { getFullChallenge } = useChallenges();
  const { getChallengeSet } = useChallengeSet();

  const toggleToolPanel = (toggle: boolean) => {
    /** Allows configuration of tool panel to hide if closed in specific components */
    if (toolPanelOpen && hideAfterClose) {
      toggleShowToolPanel(false);
      setHideAfterClose(false);
    }
    setToolPanelOpen(toggle);
  };

  const toggleShowToolPanel = (toggle: boolean) => {
    setShowToolPanel(toggle);
    // Destroy previous content when the panel is being hidden, as well as disable custom settings.
    // Key off the incoming `toggle`, not the stale `showToolPanel`
    if (!toggle) {
      setToolPanelOpen(false);
      setToolPanelContent(undefined);
      setHideAfterClose(false);
      setActivePanel(null);
    }
  };

  const toggleChallengeInfo = (challengeItem: ChallengeListItem) => {
    if (challengeItem?.challengeId) {
      if (!showToolPanel) {
        toggleShowToolPanel(true);
        toggleToolPanel(true);
        setToolPanelWidth(400);
        setHideAfterClose(true);
      }
      getFullChallenge(challengeItem.challengeId);
      setToolPanelContent(<ChallengeInfo />);
      setActivePanel('challengeInfo');
    }
  };

  const toggleChallengeDetailsInfo = (challenge: ChallengeListItem) => {
    if (challenge?.id) {
      if (!showToolPanel) {
        toggleShowToolPanel(true);
        toggleToolPanel(true);
        setToolPanelWidth(400);
        setHideAfterClose(true);
      }
      getFullChallenge(challenge.id);
      setToolPanelContent(<ChallengeDetailsInfo />);
      setActivePanel('challengeDetails');
    }
  };

  const toggleChallengeSetDetailsInfo = (challengeSet: ChallengeSet) => {
    if (challengeSet?.id) {
      if (!showToolPanel) {
        toggleShowToolPanel(true);
        toggleToolPanel(true);
        setToolPanelWidth(400);
        setHideAfterClose(true);
      }

      getChallengeSet(challengeSet.id)
        .then()
        .catch((err) => preProdLogger(err));
      setToolPanelContent(<ChallengeSetDetailsInfo />);
      setActivePanel('challengeSetDetails');
    }
  };

  const toggleJamChallengeDetailsInfo = (challenge: IJamChallenge) => {
    if (challenge?.id) {
      if (!showToolPanel) {
        toggleShowToolPanel(true);
        toggleToolPanel(true);
        setToolPanelWidth(400);
        setHideAfterClose(true);
      }

      // loadJamChallengeData({ id: challenge.id })
      //   .then()
      //   .catch((err) => preProdLogger(err));
      setToolPanelContent(<JamChallengesDetail challenge={challenge} />);
      setActivePanel('jamChallengeDetails');
    }
  };

  const toggleFacilitatorParticipantsInfo = () => {
    // Always ensure the drawer is both shown AND open
    setShowToolPanel(true);
    setToolPanelOpen(true);
    setToolPanelWidth(400);
    setHideAfterClose(true);
    setToolPanelContent(<FacilitatorParticipantsPanel />);
    setActivePanel('facilitatorParticipants');
  };

  const AI_TEAMMATE_PANEL = 'aiTeammate';

  const toggleAITeammateChat = (toggle: boolean) => {
    // Wired to the Header's toggle icon button, so it behaves as a true toggle.
    // Only treat a click as "close" when the AI Teammate panel is the one currently
    // shown; if a different panel is open, switch to (open) the AI Teammate panel
    // instead of closing the unrelated panel.
    if (!toggle || (showToolPanel && activePanel === AI_TEAMMATE_PANEL)) {
      toggleShowToolPanel(false);
      return;
    }
    if (toggle && !showToolPanel) {
      toggleShowToolPanel(true);
      toggleToolPanel(true);
      setToolPanelWidth(400);
      setHideAfterClose(true);
    }
    setToolPanelContent(<AITeammateChat compact />);
    setActivePanel(AI_TEAMMATE_PANEL);
  };

  const data: ToolPanelContextValue = {
    toolPanelContent,
    toggleToolPanel,
    toggleShowToolPanel,
    showToolPanel,
    toolPanelOpen,
    renderToolPanelContent: setToolPanelContent,
    toolPanelWidth,
    setToolPanelWidth,
    setHideAfterClose,
    toggleChallengeInfo,
    toggleChallengeDetailsInfo,
    toggleChallengeSetDetailsInfo,
    toggleJamChallengeDetailsInfo,
    toggleFacilitatorParticipantsInfo,
    toggleAITeammateChat,
    aiTeammateChatIsVisible: activePanel === AI_TEAMMATE_PANEL,
  };

  return <ToolPanelContext.Provider value={data}>{children}</ToolPanelContext.Provider>;
};

const useToolPanel = () => {
  const context = useContext(ToolPanelContext);
  if (context === undefined) {
    throw new Error('useToolPanels can only be used inside ToolPanelProvider');
  }
  return context;
};

export { ToolPanelProvider, useToolPanel };
